<template>
  <transition
    mode="out-in"
    enter-active-class="transition ease-out duration-150 transform"
    enter-from-class="opacity-0 -translate-y-4"
    leave-active-class="ease-in duration-200"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div v-if="showAlert" class="ui-alert" :class="currentType ? `alert-${currentType}` : ''">
      <button v-if="props.closable" class="order-last self-start" @click="hide">
        <UiIcon icon="x" />
      </button>
      <slot name="icon">
        <div v-if="alertIcon">
          <UiIcon size="30" :stroke-width="1.8" :icon="alertIcon" />
        </div>
      </slot>
      <div class="flex-1 text-sm">
        <slot name="title">
          <div v-if="title" class="mb-2 font-bold">{{ title }}</div>
        </slot>
        <slot>
          <div v-if="text" v-html="text" class="font-medium"></div>
        </slot>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UiIcon from './icon.vue'

const props = withDefaults(
  defineProps<{
    modelValue?: any
    type?: 'info' | 'success' | 'warning' | 'danger' | 'primary' | 'default'
    icon?: boolean | string
    title?: string
    text?: string
    duration?: number
    closable?: boolean
  }>(),
  {
    type: 'default'
  }
)

const isOpen = ref(true)
const emit = defineEmits(['update:modelValue'])

const alertTypes = ['info', 'success', 'warning', 'danger', 'primary']
const alertIcons = {
  info: 'info-circle',
  success: 'circle-check',
  warning: 'alert-triangle',
  danger: 'circle-x',
  primary: 'message-report'
}

const currentType = computed(() => alertTypes.find((el) => el === props.type) || null)
const alertIcon = computed(() => typeof props.icon === 'string' ? props.icon : (alertIcons[currentType.value] || null))
const showAlert = computed(() => props.modelValue || (typeof props.modelValue === 'undefined' && isOpen.value))

const hide = () => {
  if (typeof props.modelValue !== 'undefined') emit('update:modelValue', false)
  else isOpen.value = false
}

onMounted(() => {
  if (props.duration) setTimeout(() => hide(), props.duration)
})
</script>

<style scoped lang="scss">
.ui-alert {
  @apply box-border flex gap-4 items-center rounded-md bg-neutral-100 p-4 text-black dark:bg-neutral-800 dark:text-neutral-300;

  &.alert-info { @apply bg-[#E8F2FD] text-[#2C91F9] dark:bg-[#1E2A3B] }
  &.alert-success { @apply bg-[#E6F5EF] text-[#17AC6C] dark:bg-[#1C3329] }
  &.alert-danger { @apply bg-[#FDEBF1] text-[#FF4980] dark:bg-[#3F242C] }
  &.alert-warning { @apply bg-[#FDF5E8] text-[#FCA927] dark:bg-[#3F321F] }
  &.alert-primary { @apply rounded-lg border border-[#8B5CF6] bg-[#F3EAFF] text-[#3B0A66] dark:border-[#7C3AED] dark:bg-[#2F0A52] dark:text-white }
}
</style>
